/**
 * useCommunityShare — page-scoped helper hook for the community share flow.
 *
 * Extracted from `CommunityPage` (`presentation.md` § Component size) to keep the
 * page body to genuine assembly. Behaviour is verbatim with the former
 * `CommunityPage.handleShare`: the web `_onShareSocialUser`
 * (`community_screen.dart:865-889`), itself the canonical user-profile share
 * branch shape from `UserActionsContent.handleSocialShare:95-131` — build the
 * share content + props, prefer `navigator.share` (behind a 'protective' sheet so
 * a tap-outside doesn't close the underlying surface), and fall back to
 * `ShareSocialSheet.show`. The `AbortError` / `'Share canceled'` cancellation is
 * swallowed silently.
 *
 * Reads only the resolved `creatorUser`, the page `screenKey`, and one l10n
 * string; owns no state. Wired by `CommunityPage` to both the app-bar share
 * button and the owner empty-state "Share to get subscribers" button.
 */
import { useCallback } from 'react';
import toast from 'react-hot-toast';

import { useLocale } from '@/presentation/contexts/LocaleContext';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { ShareSocialSheet } from '@/presentation/feature/content/components/sheets/shareSocial/ShareSocialSheet';
import { buildSocialShareProps } from '@/presentation/feature/share/buildSocialShareProps';

import type { UserEntity } from '@/domain/entities/user/userEntity';
import { generateUserProfileShareDataUseCase } from '@/domain/usecases/share/generateUserProfileShareDataUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export const useCommunityShare = (
  creatorUser: UserEntity | null,
  screenKey: string,
): { handleShare: () => Promise<void> } => {
  const { l10n } = useLocale();

  const handleShare = useCallback(async (): Promise<void> => {
    const creator = creatorUser;
    if (!creator) return;

    const shareContent = await generateUserProfileShareDataUseCase.execute(creator);
    const socialShareProps = buildSocialShareProps(creator, shareContent);

    if (navigator.share) {
      try {
        // Open a 'protective' sheet so tapping outside the native share modal
        // does not close the underlying surface.
        SheetService.show({
          title: '',
          content: <div />,
          container: null,
          screenKey,
          fixedHeight: '0vh',
          width: '0vw',
          closeOnClickOutside: false,
        });

        const shareData: ShareData = shareContent.file
          ? {
              title: shareContent.title,
              text: `${shareContent.text}\n\n${shareContent.shareUrl}`,
              files: [shareContent.file],
            }
          : {
              title: shareContent.title,
              text: shareContent.text,
              url: shareContent.shareUrl,
            };

        await navigator.share(shareData);
        SheetService.close();
      } catch (error) {
        SheetService.close();
        const err = error instanceof Error ? error : new Error(String(error));
        if (err.message === 'Share canceled' || err.name === 'AbortError') {
          return;
        }
        appLogger.error('Error sharing community creator profile', 'CommunityPage.handleShare', {
          error: err,
        });
        toast.error(l10n.userActions.socialShareError);
        ShareSocialSheet.show(socialShareProps, null, screenKey);
      }
    } else {
      // Browser doesn't support the Web Share API — fall back to the share sheet.
      ShareSocialSheet.show(socialShareProps, null, screenKey);
    }
  }, [creatorUser, screenKey, l10n.userActions.socialShareError]);

  return { handleShare };
};
